import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { provideNgxMask } from 'ngx-mask';
import { SolicitacaoService } from 'src/app/core/apis/solicitacao/solicitacao.service';
import { Credor } from 'src/app/core/model/credor';
import { OrdemBaixa } from 'src/app/core/model/ordem';
import { Titular } from 'src/app/core/model/titular';
import { DialogService } from 'src/app/core/service/dialog.service';
import { ErrorHandlerService } from 'src/app/core/service/error-handler.service';
import { OrdemBuilderService } from 'src/app/core/service/ordem-builder.service';
import { AcaoButtonsComponent } from "src/app/shared/components/acao-buttons/acao-buttons.component";
import { ListaCredoresComponent } from 'src/app/shared/components/lista-credores/lista-credores.component';
import { ListaTitularesComponent } from 'src/app/shared/components/lista-titulares/lista-titulares.component';
import { NroProcessoInputComponent } from 'src/app/shared/components/nro-processo-input/nro-processo-input.component';
import { SlaSelectorComponent } from "src/app/shared/components/sla-selector/sla-selector.component";
import { Globals } from 'src/app/shared/helpers/globals';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';

@Component({
    selector: 'app-baixa-anotacao',
    templateUrl: './baixa-anotacao.component.html',
    styleUrls: ['./baixa-anotacao.component.css'],
    providers: [provideNgxMask()],
    imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatMenuModule,
    MatIconModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatButtonModule,
    FormsModule,
    ListaCredoresComponent,
    NroProcessoInputComponent,
    SlaSelectorComponent,
    AcaoButtonsComponent,
    ListaTitularesComponent
]

})
export class BaixaAnotacaoComponent implements OnInit, OnChanges {

  @Input() tokenIam: string;

  @Input() ordemBaixaInput: OrdemBaixa;

  @ViewChild(NroProcessoInputComponent) nroProcessoInput!: NroProcessoInputComponent;
  @ViewChild(ListaTitularesComponent) listaTitularesComponent!: ListaTitularesComponent;
  @ViewChild(ListaCredoresComponent) listaCredoresComponent!: ListaCredoresComponent;

  form: UntypedFormGroup;

  titulares: Titular[];
  credores: Credor[];

  envolvidos: Titular[] = [];
  envolvidosAutor: Titular[] = [];
  envolvidosReu: Titular[] = [];

  constructor(
    private globals: Globals,
    private solicitacaoService: SolicitacaoService,
    private ordemBuilderService: OrdemBuilderService,
    private dialogService: DialogService,
    private errorHandler: ErrorHandlerService,
    private router: Router,
    fb: UntypedFormBuilder
  ) {
    this.form = fb.group({
      nroProcesso: [""],
      nroSla: ["120", Validators.required],
      autor: ["", Validators.required],
      reu: ["", Validators.required],
    });

    this.titulares = [];
    this.credores = [];
  }

  ngOnInit() {
    this.envolvidosAutor = [...this.envolvidos];
    this.envolvidosReu = [...this.envolvidos];
  }

  ngOnChanges(): void {
    if (this.ordemBaixaInput) {
      this.nroProcesso.setValue(this.ordemBaixaInput.numeroProcesso);
      this.nroProcesso.disable();
      this.titulares = this.ordemBaixaInput.titulares;
      this.globals.subtractFromLoading();
    }
  }

  private atualizarEnvolvidos() {
    const titulares = [...this.titulares];
    const credoresUnicos = this.getDistinctCredores();

    this.envolvidos = [...titulares, ...credoresUnicos];
    this.filterEnvolvidos();
  }

  private getDistinctCredores() {
    return this.credores.filter(
      (credor, index, self) =>
        self.findIndex(c => c.nome === credor.nome) === index
    );
  }

  public filterEnvolvidos() {
    const autorSelecionado = this.autor?.value;
    const reuSelecionado = this.reu?.value;

    this.validateSeExcluido(autorSelecionado, reuSelecionado);

    this.envolvidosAutor = this.getListaPara(reuSelecionado);
    this.envolvidosReu = this.getListaPara(autorSelecionado);
  }

  private validateSeExcluido(autorSelecionado: any, reuSelecionado: any) {
    const autorExiste = this.envolvidos.some(e => e.nome === autorSelecionado);
    const reuExiste = this.envolvidos.some(e => e.nome === reuSelecionado);

    if (!autorExiste)
      this.autor?.reset();
    if (!reuExiste)
      this.reu?.reset();
  }

  private getListaPara(opostoSelecionado: string): any[] {
    if (!opostoSelecionado) return this.envolvidos;
    if (this.isTitular(opostoSelecionado)) return this.getDistinctCredores();
    if (this.isCredor(opostoSelecionado)) return this.titulares;
    return this.envolvidos;
  }

  private isTitular(nome: string): boolean {
    return this.titulares.some(t => t.nome === nome);
  }

  private isCredor(nome: string): boolean {
    return this.credores.some(c => c.nome === nome);
  }

  public limparAutorReu() {
    this.autor?.reset();
    this.reu?.reset();
    this.filterEnvolvidos();
  }

  public baixarOrdem() {
    if(!this.isFormValid()){
      this.touchForm();
      return;
    };
    this.globals.addToLoading("cadastrar-ordem");

    const ordem = this.ordemBuilderService.buildBaixa(this.nroProcesso.value, this.titulares, this.form.value, this.credores);
    this.solicitacaoService.saveSolicitacao(ordem, this.tokenIam).subscribe({
      next: (response) => {
        this.globals.subtractFromLoading();
        this.globals.showSnackBar("Baixa enviada com sucesso!", ["snackbar-success"]);
        this.router.navigate(["/ordem"]);
      },
      error: (error) => {
        this.globals.subtractFromLoading();
        this.errorHandler.handle(error);
      }
    });
  }

  private touchForm() {
    if(this.nroProcesso.invalid)this.nroProcessoInput.control.markAsTouched();
    if(this.isTitularesEmpty()){
      this.listaTitularesComponent.nome.setErrors({titularesEmpty: true})
      this.listaTitularesComponent.documento.setErrors({titularesEmpty: true})
      this.listaTitularesComponent.form.markAllAsTouched();
    }
    if(this.isCredoresEmpty()){
      this.listaCredoresComponent.nome.setErrors({credoresEmpty: true})
      this.listaCredoresComponent.valor.setErrors({credoresEmpty: true})
      this.listaCredoresComponent.dataDivida.setErrors({credoresEmpty: true})
      this.listaCredoresComponent.form.markAllAsTouched();
    }
    if(this.form.invalid && !this.isTitularesEmpty()  && !this.isCredoresEmpty()){
      this.form.markAllAsTouched();
    }
  }

  public isFormValid(): boolean{
    return this.form.valid && !this.isTitularesEmpty() && !this.isCredoresEmpty();
  }

  private isTitularesEmpty(): boolean{
    return this.titulares.length == 0;
  }

  private isCredoresEmpty(): boolean{
    return this.credores.length == 0;
  }

  public onTitularesChange(titulares: Titular[]) {
    this.titulares = titulares;
    this.atualizarEnvolvidos();
  }

  public onCredoresChange(credores: Credor[]) {
    this.credores = credores;
    this.atualizarEnvolvidos();
  }

  public cancelamentoDialog(): void {
    this.dialogService.showConfirmation(['Deseja cancelar a ação?']).subscribe(confirm => {
      if (confirm) {
        this.router.navigate(['/ordem']);
      }
    });
  }

  get nroProcesso() {
    return this.form.get("nroProcesso");
  }

  get nroSla() {
    return this.form.get("nroSla");
  }

  get autor() {
    return this.form.get("autor");
  }

  get reu() {
    return this.form.get("reu");
  }

}


